import React, { useState, useEffect, useRef } from 'react';

/**
 * COMPONENTE: ANECDOTES TICKER (CINTILLO INFERIOR ~250px)
 * ------------------------------------------------------------------
 * Diseñado específicamente para incrustación sobre video en vivo (OBS / vMix).
 * Cumple con:
 * 1. Altura calibrada a ~250px para 3 a 5 líneas de anécdota.
 * 2. Posición vertical dinámica (offsetY) via inline style (`bottom: ${offsetY}px`).
 * 3. Animación de entrada/salida deslizante (slideIn / slideOut).
 * 4. Efecto de máquina de escribir a 35ms por letra.
 * 5. Logotipos institucionales oficiales y badge "TU MEJOR ANÉCDOTA".
 * ------------------------------------------------------------------
 */
export const AnecdotesTicker = ({ data, offsetY = 0 }) => {
    const [verticalOffset, setVerticalOffset] = useState(offsetY);
    const [flowState, setFlowState] = useState('idle'); // 'idle' | 'entering' | 'typing' | 'visible' | 'fadingOut'
    const [messageData, setMessageData] = useState(null);
    const [pendingData, setPendingData] = useState(null);
    const [displayedText, setDisplayedText] = useState('');

    const flowStateRef = useRef(flowState);
    useEffect(() => {
        flowStateRef.current = flowState;
    }, [flowState]);

    // Actualizar offset vertical si llega vía prop directa
    useEffect(() => {
        if (typeof offsetY === 'number' && Number.isFinite(offsetY)) {
            setVerticalOffset(Math.min(1000, Math.max(-1000, offsetY)));
        }
    }, [offsetY]);

    // 1. Recepción y encolamiento de datos desde WebSocket / Parent
    useEffect(() => {
        if (!data) return;

        // A. Configuración en caliente de Posición Vertical (offsetY)
        if (data.action === 'config' && data.offsetY !== undefined && typeof data.offsetY === 'number' && Number.isFinite(data.offsetY)) {
            setVerticalOffset(Math.min(1000, Math.max(-1000, data.offsetY)));
            return;
        }

        // B. Comando de Ocultar / Limpiar Pantalla
        if (data.action === 'hide' || data.action === 'clear' || (!data.message && !data.content && !data.text)) {
            setPendingData(null);
            if (flowStateRef.current !== 'idle' && flowStateRef.current !== 'fadingOut') {
                setFlowState('fadingOut');
            }
            return;
        }

        // C. Normalización de anécdota
        const rawText = data.message || data.content || data.text || '';
        const rawSender = data.sender || 'Anónimo';
        const normalized = {
            id: data.id,
            text: rawText,
            sender: rawSender
        };

        // D. Comando de Mostrar Anécdota
        if (flowStateRef.current === 'idle') {
            setMessageData(normalized);
            setFlowState('entering');
        } else {
            // Si ya hay una proyectándose, se desvanece la actual y se encola la siguiente
            setPendingData(normalized);
            if (flowStateRef.current !== 'fadingOut') {
                setFlowState('fadingOut');
            }
        }
    }, [data]);

    // 2. Control de transiciones de entrada y salida
    useEffect(() => {
        let timeout;
        if (flowState === 'entering') {
            // Coincide con la duración de la animación slideIn en SASS (500ms)
            timeout = setTimeout(() => {
                setFlowState('typing');
            }, 500);
        } else if (flowState === 'fadingOut') {
            // Coincide con la duración de la animación slideOut en SASS (500ms)
            timeout = setTimeout(() => {
                if (pendingData) {
                    setMessageData(pendingData);
                    setPendingData(null);
                    setFlowState('entering');
                } else {
                    setMessageData(null);
                    setFlowState('idle');
                }
                setDisplayedText('');
            }, 500);
        }

        return () => clearTimeout(timeout);
    }, [flowState, pendingData]);

    // 3. Efecto Máquina de Escribir (35ms por letra)
    useEffect(() => {
        if (flowState === 'typing' && messageData) {
            let currentIndex = 0;
            const fullText = messageData.text;
            setDisplayedText('');

            const interval = setInterval(() => {
                setDisplayedText(fullText.substring(0, currentIndex + 1));
                currentIndex++;

                if (currentIndex >= fullText.length) {
                    clearInterval(interval);
                    setFlowState('visible');
                }
            }, 35); // 35ms por letra según requerimiento de la HU

            return () => clearInterval(interval);
        }
    }, [flowState, messageData]);

    if (flowState === 'idle') return null;

    return (
        <div
            className="anecdotes-ticker"
            style={{ bottom: `${verticalOffset}px` }}
            data-testid="anecdotes-ticker-container"
        >
            <div className={`anecdotes-ticker__banner anecdotes-ticker__banner--${flowState}`}>
                {/* Logo Institucional Izquierdo */}
                <div className="anecdotes-ticker__logo-wrapper anecdotes-ticker__logo-wrapper--left">
                    <img
                        src="/img/overlays/messages/message-adsl-logo-interaktia.png"
                        alt="Logo Barrio 9 Interaktia"
                        className="anecdotes-ticker__logo"
                    />
                </div>

                {/* Contenido Central: Título, Remitente y Texto Dinámico */}
                <div className="anecdotes-ticker__content">
                    <div className="anecdotes-ticker__header">
                        <span className="anecdotes-ticker__badge">
                            <i className="bi bi-chat-quote-fill me-1"></i> TU MEJOR ANÉCDOTA
                        </span>
                        {messageData?.sender && (
                            <span className="anecdotes-ticker__sender">
                                De: <i className="bi bi-person-fill me-1"></i> {messageData.sender}
                            </span>
                        )}
                    </div>
                    <div className="anecdotes-ticker__text" title={messageData?.text}>
                        <span className="anecdotes-ticker__quote">&ldquo;</span>
                        {displayedText}
                        <span className="anecdotes-ticker__quote">&rdquo;</span>
                        {flowState === 'typing' && (
                            <span className="anecdotes-ticker__cursor">|</span>
                        )}
                    </div>
                </div>

                {/* Logo Derecho ADSL */}
                <div className="anecdotes-ticker__logo-wrapper anecdotes-ticker__logo-wrapper--right">
                    <img
                        src="/img/overlays/messages/message-adsl-logo-barrio9.png"
                        alt="Logo Barrio 9 Interaktia"
                        className="anecdotes-ticker__logo anecdotes-ticker__logo--badge"
                    />
                </div>
            </div>
        </div>
    );
};

export default AnecdotesTicker;
